import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import riskDocumentAnswerService from "../../services/risk-document-answers/risk-document-answers.service";
import {
  RiskDocumentAnswerDto,
  RiskLevelEnum,
} from "../../services/risk-document-answers/risk-document-answers.dto";
import { FormikErrors, useFormik } from "formik";
import { Button } from "primereact/button";
import { getRiskDocumentStatusClass, getRiskDocumentStatusText } from "./RiskEvaluation";
import ArfMultipleFileUpload from "../../components/ArfComponents/ArfMultipleFileUpload/ArfMultipleFileUpload";
import { useAuth } from "../../utils/auth";
import { ApplicationRoles } from "../../services/roles/roles.dto";
import { Dropdown } from "primereact/dropdown";
import { FieldErrorMessage } from "../../utils/formValidator";
import { ApplicationFormDto } from "../../services/application/application.dto";
import { useEffect } from "react";
import { useAxiosContext } from "../..";

const RiskEvaluationDetail = () => {
  const { t } = useTranslation();
  const toaster = useAxiosContext();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth.getUser();
  const selectedRiskDocument: RiskDocumentAnswerDto =
    location.state && location.state.selectedRiskDocument;
  const applicationForm: ApplicationFormDto | undefined =
    location.state && location.state.applicationForm;
  const riskDocumentsQueryKey: string[] = location.state && location.state.riskDocumentsQueryKey;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!selectedRiskDocument || !applicationForm || !riskDocumentsQueryKey) {
      navigate("/app/risk-evaluation");
    }
  }, [selectedRiskDocument, applicationForm, riskDocumentsQueryKey]);

  const formik = useFormik<RiskDocumentAnswerDto>({
    initialValues: selectedRiskDocument,
    enableReinitialize: true,
    validate: data => {
      let errors: FormikErrors<RiskDocumentAnswerDto> = {};

      if (data.riskLevel === undefined || data.riskLevel === null) {
        errors.riskLevel = t("Required");
      }
      if (
        data.applicationRiskDocumentAnswers === undefined ||
        data.applicationRiskDocumentAnswers.length === 0
      ) {
        errors.applicationRiskDocumentAnswers = t("Required");
      }

      if (
        data.applicationRiskDocumentAnswers === undefined ||
        data.applicationRiskDocumentAnswers.length === 0
      ) {
        errors.applicationRiskDocumentAnswers = t("Required");
      }
      return errors;
    },
    onSubmit: data => {
      riskDocumentAnswerService
        .AddAnswer(
          {
            applicationRiskDocumentId: data.id,
            fileIds: data.applicationRiskDocumentAnswers.map(item => item.fileId),
            riskLevel: data.riskLevel,
          },
          { blockUI: true }
        )
        .then(response => {
          if (response.result) {
            navigate(`/app/risk-evaluation/${applicationForm?.uid}`);
            queryClient.invalidateQueries({
              queryKey: riskDocumentsQueryKey,
            });
            toaster.showMessage(t("AnswerSuccessfullyRecorded"), "success");
          }
        });
    },
  });

  const options = [
    { label: t("Low"), value: RiskLevelEnum.Low },
    { label: t("Medium"), value: RiskLevelEnum.Medium },
    { label: t("High"), value: RiskLevelEnum.High },
  ];

  return (
    <div className="px-3 md:px-6 py-3 flex flex-column gap-1 mt-2">
      <div className="flex justify-content-between">
        <div
          className="flex flex-row gap-2 mb-3 w-max hover-opacity-70 cursor-pointer"
          onClick={() => {
            navigate(`/app/risk-evaluation/${applicationForm?.uid}`);
          }}
        >
          <div className="flex align-items-center">
            <i className="pi pi-arrow-left text-base"></i>
          </div>
          <div className="font-medium text-xl">{t("RiskEvaluation")}</div>
        </div>
        <div className="flex justify-content-end mb-2">
          <div className={`${getRiskDocumentStatusClass(selectedRiskDocument)} status-container`}>
            {t(getRiskDocumentStatusText(selectedRiskDocument))}
          </div>
        </div>
      </div>

      <form
        onSubmit={e => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="applicant-card surface-0 p-5">
          <div className="flex md:flex-row flex-column md:justify-content-between md:align-items-baseline gap-3">
            <span className="text-lg text-color font-semibold">
              {selectedRiskDocument.riskTemplateDocument.file?.name}
            </span>
            <div className="flex justify-content-end">
              <div
                className="flex align-items-center gap-2 primary-color px-4 py-2 border-dashed border-1 border-round-2xl cursor-pointer w-max"
                style={{ backgroundColor: "#0085FF26" }}
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_BASE_URL}files/${selectedRiskDocument.riskTemplateDocument.fileId}?mode=inline`,
                    "_blank"
                  )
                }
              >
                <i className="pi pi-download" />
                <span className="white-space-nowrap font-semibold">
                  {t("DownloadExampleDocument")}
                </span>
              </div>
            </div>
          </div>
          <ArfMultipleFileUpload
            officialFileList={formik.values.applicationRiskDocumentAnswers.map(item => item.file)}
            setOfficialFileList={files => {
              formik.setFieldValue(
                "applicationRiskDocumentAnswers",
                files.map(item => ({
                  applicationRiskDocumentId: formik.values.id,
                  fileId: item.id,
                  file: item,
                }))
              );
            }}
            isItDetailPage={false}
          />
          {FieldErrorMessage(formik, "applicationRiskDocumentAnswers")}

          <div className="grid m-0 mt-4">
            <div className="col-12 md:col-6 mt-2 pl-0 lg:pr-8">
              <label className="block mb-2 text-color font-semibold">{t("RiskLevel")}</label>
              <Dropdown
                id="riskLevel"
                options={options}
                value={formik.values.riskLevel}
                onChange={e => formik.setFieldValue("riskLevel", e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder="Seçiniz"
                className="w-full"
              />
              {FieldErrorMessage(formik, "riskLevel")}
            </div>
          </div>
        </div>
        <div className="flex justify-content-end w-full mt-5">
          <Button label={t("Save")} className="w-12rem" type="submit" />
        </div>
      </form>
    </div>
  );
};

export default RiskEvaluationDetail;
